import React, { lazy, Suspense, useEffect } from 'react';
import { SUBSCRIPTION_STATUS_ENUM } from '@Saral-Influencer-Marketing/saral-utils/src/constants';
import * as Sentry from '@sentry/react';
import Gleap from 'gleap';
import { useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import RegisterTeam from '@/components/Auth/RegisterTeam';

import FallbackLoader from '../components/common-components/Loaders/FallbackLoader';
import MainFallbackLoader from '../components/common-components/Loaders/MainFallbackloader';
import MaintenancePage from '../components/common-components/MaintenancePage/MaintenancePage';
import Dashboard from '../components/Dashboard/Dashboard';
import InfluencerSigning from '../components/Dashboard/Lists/components/ContractsBox/InfluencerSigning/InfluencerSigning';
import BlackListedInfluencers from '../components/Dashboard/settings/BlackListedInfluencers';
import PDFEditor from '../components/Dashboard/settings/components/CustomContracts/PDFEditor/PDFEditor';
import LibraryDetail from '../components/Dashboard/SocialListening/LibraryDetail';
import SocialListeningLibraries from '../components/Dashboard/SocialListening/SocialListeningLibraries';
import { GoBack } from '../components/GoBack';
import BookingSuccess from '../components/StrategySession/BookingSuccess/BookingSuccess';
import BookSession from '../components/StrategySession/BookSession/BookSession';
import { isAuthenticated, isDiscoveryBlockedHardcoded } from '../utils/auth-utils';
import { isJsrStorageKey, isLocalhost } from '../utils/constants';
import { getStorageObject, lazyRetry } from '../utils/global';
import PrivateRoute from './PrivateRoute';

const Login = lazy(() => lazyRetry(() => import('../components/Auth/Login'), 'auth-login'));
const LoginTeam = lazy(() => lazyRetry(() => import('../components/Auth/LoginTeam'), 'auth-login-team'));
const MasterLogin = lazy(() => lazyRetry(() => import('../components/Auth/MasterLogin'), 'master-login'));
const Register = lazy(() => lazyRetry(() => import('../components/Auth/Register'), 'auth-register'));
const EmailVerification = lazy(() =>
  lazyRetry(() => import('../components/Auth/EmailVerification'), 'email-verification')
);
const EmailVerified = lazy(() => lazyRetry(() => import('../components/Auth/EmailVerified'), 'email-verified'));
const ForgotPassword = lazy(() => lazyRetry(() => import('../components/Auth/ForgotPassword'), 'forgot-password'));
const ResetPassword = lazy(() => lazyRetry(() => import('../components/Auth/ResetPassword'), 'reset-password'));
const HomeView = lazy(() => lazyRetry(() => import('../components/Dashboard/home/HomeView'), 'home-view'));
const Discovery = lazy(() => lazyRetry(() => import('../components/Dashboard/discovery/Discovery'), 'discovery'));
const Relationship = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/Relationships/Relationship'), 'relationship')
);
const List = lazy(() => lazyRetry(() => import('../components/Dashboard/Lists/List'), 'list'));
const ListDetail = lazy(() => lazyRetry(() => import('../components/Dashboard/Lists/ListDetail'), 'list-detail'));
const Profile = lazy(() => lazyRetry(() => import('../components/Dashboard/Lists/Profile'), 'profile'));
const EmailDripsProWrapper = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/email/EmailDripsProWrapper'), 'email-drop')
);
const EmailOverviewWrapper = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/email/EmailOverviewWrapper'), 'email-overview')
);
const Settings = lazy(() => lazyRetry(() => import('../components/Dashboard/settings/Settings'), 'settings'));
const PageNotFound = lazy(() => lazyRetry(() => import('../components/Dashboard/PageNotFound'), 'page-not-found'));
const Inbound = lazy(() => lazyRetry(() => import('../components/Dashboard/inbound/inbound'), 'inboud'));
const UploadResources = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/inbound/UploadResources'), 'upload-resources')
);
const AppPerformance = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/performance/AppPerformance'), 'app-performance')
);
const AdminPerformance = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/performance/AdminPerformance'), 'admin-performance')
);
const AffiliatePayments = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/affiliatePayments/AffiliatePayments'), 'affiliate-payment')
);

const SocialListeningCampaigns = lazy(() =>
  lazyRetry(
    () => import('../components/Dashboard/SocialListening/SocialListeningCampaigns'),
    'social-listening-campaigns'
  )
);

const AddCampaign = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/SocialListening/AddCampaign'), 'social-listening-add-campaign')
);

const CampaignDetail = lazy(() =>
  lazyRetry(() => import('../components/Dashboard/SocialListening/CampaignDetail'), 'social-listening-campaign-details')
);

const Inbox = lazy(() => lazyRetry(() => import('../components/Dashboard/Inbox/Inbox'), 'inbox'));

const BlockError = lazy(() =>
  lazyRetry(() => import('../components/common-components/ErrorFallback/IPBlockError'), 'ip-block-error')
);

const GetStarted = lazy(() => lazyRetry(() => import('../components/Dashboard/getStarted/GetStarted'), 'get-started'));

const Routing = () => {
  // TODO: MUI
  const { userDetails, isBetaUser } = useSelector((state) => state.user);

  useEffect(() => {
    const storageObject = getStorageObject(isJsrStorageKey);

    if (!isLocalhost) {
      // Initialize Gleap only if not on localhost
      Gleap.initialize(import.meta.env.REACT_APP_GLEAP_API_KEY);
    }
  }, []);

  useEffect(() => {
    const storageObject = getStorageObject(isJsrStorageKey);

    if (isAuthenticated() && userDetails?._id && !storageObject?.isJsrLogin) {
      if (!Gleap.isUserIdentified()) {
        Gleap.identify(`${userDetails?.teamMemberId}`, {
          name: userDetails?.first_name,
          email: userDetails?.email,
          plan: userDetails?.plan?.name,
        });
      }

      Sentry.setUser({
        id: userDetails?._id,
        email: userDetails?.email,
        ip_address: 'auto',
      });
    }
  }, [userDetails]);

  const isTrialUser = userDetails?.subscriptionStatus === SUBSCRIPTION_STATUS_ENUM.trial;

  if (import.meta.env.REACT_APP_MAINTENANCE_MODE === 'true') {
    return <MaintenancePage />;
  }

  return (
    <Router>
      <Suspense fallback={<FallbackLoader />}>
        <Routes>
          <Route path="/influencer-signing/:userId/:contractId" element={<InfluencerSigning />} />
          <Route
            path="/"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <Login />
              </PrivateRoute>
            }
          />
          <Route
            path="/login-team"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <LoginTeam />
              </PrivateRoute>
            }
          />
          <Route path="/integration-complete" element={<GoBack />} />
          <Route
            path="/jsr"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <MasterLogin />
              </PrivateRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <Register />
              </PrivateRoute>
            }
          />
          <Route
            path="/try-free"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <Register />
              </PrivateRoute>
            }
          />
          <Route
            path="/register-team"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <RegisterTeam />
              </PrivateRoute>
            }
          />
          <Route
            path="/auth/verify"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <Register />
              </PrivateRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <ForgotPassword />
              </PrivateRoute>
            }
          />
          <Route
            path="/reset-password/:userId/:token"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <ResetPassword />
              </PrivateRoute>
            }
          />
          <Route
            path="error"
            element={
              <Suspense fallback={<MainFallbackLoader />}>
                <BlockError />
              </Suspense>
            }
          />
          <Route
            path="/admin/performance"
            element={
              <Suspense fallback={<MainFallbackLoader />}>
                <AdminPerformance />
              </Suspense>
            }
          />
          <Route path="/verify/email/:userId/:token" element={<EmailVerification />} />
          <Route
            path="/email-verified/:userId/:token"
            element={
              <PrivateRoute condition={!isAuthenticated()} navigateTo="/dashboard">
                <EmailVerified />
              </PrivateRoute>
            }
          />
          <Route
            path="/book-session"
            element={
              <PrivateRoute>
                <BookSession />
              </PrivateRoute>
            }
          />
          <Route
            path="/book-session-success"
            element={
              <PrivateRoute>
                <BookingSuccess />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }>
            <Route
              path=""
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <HomeView />
                </Suspense>
              }
            />
            <Route
              path="get-started"
              element={
                <PrivateRoute>
                  <Suspense fallback={<MainFallbackLoader />}>
                    <GetStarted />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="discovery"
              element={
                <PrivateRoute condition={!isDiscoveryBlockedHardcoded(userDetails)} navigateTo="/dashboard">
                  <Suspense fallback={<MainFallbackLoader />}>
                    <Discovery />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="relationship"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <Relationship />
                </Suspense>
              }
            />
            <Route
              path="list"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <List />
                </Suspense>
              }
            />
            <Route
              path="list/detail/:id"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <ListDetail />
                </Suspense>
              }
              key={document.location.href}
            />
            <Route
              path="profile/:listId/:id"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <Profile />
                </Suspense>
              }
            />
            <Route
              path="email-drips"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <EmailDripsProWrapper />
                </Suspense>
              }
            />
            <Route
              path="email-templates/:id"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <EmailOverviewWrapper />
                </Suspense>
              }
            />
            <Route path="settings">
              <Route
                index
                element={
                  <Suspense fallback={<MainFallbackLoader />}>
                    <Settings />
                  </Suspense>
                }
              />
              <Route path="blacklisted" element={<BlackListedInfluencers />} />
              <Route path="custom-contracts">
                <Route
                  path="editor"
                  element={
                    <Suspense fallback={<MainFallbackLoader />}>
                      <PDFEditor />
                    </Suspense>
                  }
                />
                <Route
                  path="editor/:templateId"
                  element={
                    <Suspense fallback={<MainFallbackLoader />}>
                      <PDFEditor />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
            <Route
              path="applications"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <Inbound />
                </Suspense>
              }
            />

            <Route
              path="inbox"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <Inbox />
                </Suspense>
              }
            />

            <Route
              path="social/campaigns"
              element={
                <PrivateRoute>
                  <Suspense fallback={<MainFallbackLoader />}>
                    <SocialListeningCampaigns />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="social/libraries"
              element={
                <PrivateRoute>
                  <Suspense fallback={<MainFallbackLoader />}>
                    <SocialListeningLibraries />
                  </Suspense>
                </PrivateRoute>
              }
            />

            <Route
              path="social/libraries/:id"
              element={
                <PrivateRoute>
                  <Suspense fallback={<MainFallbackLoader />}>
                    <LibraryDetail />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="social/add"
              element={
                <PrivateRoute>
                  <Suspense fallback={<MainFallbackLoader />}>
                    <AddCampaign />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="social/:id"
              element={
                <PrivateRoute>
                  <Suspense fallback={<MainFallbackLoader />}>
                    <CampaignDetail />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="performance"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <AppPerformance />
                </Suspense>
              }
            />
            <Route
              path="applications/uploads"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <UploadResources />
                </Suspense>
              }
            />
            <Route
              path="payments"
              element={
                <Suspense fallback={<MainFallbackLoader />}>
                  <AffiliatePayments />
                </Suspense>
              }
            />
            <Route path="*" element={<PageNotFound />} />
          </Route>
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default Sentry.withProfiler(Routing);
